import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import {
  ArrowLeft,
  Download,
  Edit,
  FileText,
  Filter,
  Loader2,
  RefreshCw,
  Star,
  Trash2,
  Upload,
  X,
} from "lucide-react";
import { requireAdmin } from "@/lib/admin-guard";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import {
  listAdminLibraryDocuments,
  createLibraryDocument,
  updateLibraryDocument,
  deleteLibraryDocument,
  getLibraryDocumentDownloadUrl,
  LIB_DOC_MAX_BYTES,
  LIB_DOC_ALLOWED_EXTENSIONS,
  type LibraryDocument,
  type LibraryVisibility,
  type LibraryReviewStatus,
} from "@/lib/library-documents.functions";
import { uploadLibraryDocumentFile } from "@/lib/library-documents-upload";

export const Route = createFileRoute("/admin_/library")({
  ssr: false,
  head: () => ({
    meta: [
      { title: "Library — Admin" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  beforeLoad: requireAdmin,
  component: AdminLibraryPage,
  errorComponent: ({ error }) => (
    <div className="p-8 text-destructive">{error instanceof Error ? error.message : "Page unavailable"}</div>
  ),
  notFoundComponent: () => <div className="p-8">Not found.</div>,
});

const VISIBILITY_OPTS: { value: LibraryVisibility; label: string }[] = [
  { value: "draft", label: "Draft (hidden)" },
  { value: "public", label: "Public (everyone)" },
  { value: "published", label: "Members area (signed-in)" },
  { value: "member_only", label: "Founding Members only" },
];

const REVIEW_OPTS: { value: LibraryReviewStatus; label: string }[] = [
  { value: "not_reviewed", label: "Not reviewed" },
  { value: "reviewed", label: "Reviewed" },
  { value: "approved", label: "Approved" },
];

const ACCEPT = LIB_DOC_ALLOWED_EXTENSIONS.map((e) => "." + e).join(",");
const MAX_MB = Math.round(LIB_DOC_MAX_BYTES / (1024 * 1024));

function bytesFmt(n: number) {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function visibilityLabel(v: LibraryVisibility) {
  return VISIBILITY_OPTS.find((o) => o.value === v)?.label ?? v;
}

function visibilityTone(v: LibraryVisibility): "default" | "secondary" | "outline" | "destructive" {
  if (v === "draft") return "outline";
  if (v === "public") return "default";
  if (v === "member_only") return "secondary";
  return "secondary";
}

function reviewLabel(r: LibraryReviewStatus) {
  return REVIEW_OPTS.find((o) => o.value === r)?.label ?? r;
}

type FormState = {
  title: string;
  description: string;
  category: string;
  tags: string; // comma-separated in UI
  author_source: string;
  visibility: LibraryVisibility;
  is_featured: boolean;
  review_status: LibraryReviewStatus;
};

const EMPTY_FORM: FormState = {
  title: "",
  description: "",
  category: "",
  tags: "",
  author_source: "",
  visibility: "draft",
  is_featured: false,
  review_status: "not_reviewed",
};

function AdminLibraryPage() {
  const qc = useQueryClient();
  const listFn = useServerFn(listAdminLibraryDocuments);
  const createFn = useServerFn(createLibraryDocument);
  const updateFn = useServerFn(updateLibraryDocument);
  const deleteFn = useServerFn(deleteLibraryDocument);
  const downloadFn = useServerFn(getLibraryDocumentDownloadUrl);

  const { data: docs = [], isLoading, refetch, isFetching } = useQuery({
    queryKey: ["admin", "library-documents"],
    queryFn: () => listFn(),
  });

  const [query, setQuery] = useState("");
  const [visibilityFilter, setVisibilityFilter] = useState<LibraryVisibility | "all">("all");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return docs.filter((d) => {
      if (visibilityFilter !== "all" && d.visibility !== visibilityFilter) return false;
      if (!q) return true;
      const hay = `${d.title} ${d.description ?? ""} ${d.category ?? ""} ${d.tags.join(" ")} ${
        d.author_source ?? ""
      }`.toLowerCase();
      return hay.includes(q);
    });
  }, [docs, query, visibilityFilter]);

  // Upload dialog state
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadForm, setUploadForm] = useState<FormState>(EMPTY_FORM);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const uploadInputRef = useRef<HTMLInputElement>(null);

  function resetUpload() {
    setUploadForm(EMPTY_FORM);
    setUploadFile(null);
    if (uploadInputRef.current) uploadInputRef.current.value = "";
  }

  async function handleCreate() {
    if (!uploadFile) {
      uploadInputRef.current?.click();
      return;
    }
    if (!uploadForm.title.trim()) return toast.error("Title is required");
    setUploading(true);
    try {
      const up = await uploadLibraryDocumentFile(uploadFile);
      await createFn({
        data: {
          title: uploadForm.title.trim(),
          description: uploadForm.description.trim() || null,
          category: uploadForm.category.trim() || null,
          tags: uploadForm.tags
            .split(",")
            .map((t) => t.trim())
            .filter(Boolean),
          author_source: uploadForm.author_source.trim() || null,
          file_path: up.path,
          file_name: up.name,
          file_size: up.size,
          visibility: uploadForm.visibility,
          is_featured: uploadForm.is_featured,
          review_status: uploadForm.review_status,
        },
      });
      toast.success("Document uploaded");
      setUploadOpen(false);
      resetUpload();
      qc.invalidateQueries({ queryKey: ["admin", "library-documents"] });
    } catch (e: any) {
      toast.error(e?.message ?? "Upload failed");
    } finally {
      setUploading(false);
    }
  }

  // Edit dialog state
  const [editing, setEditing] = useState<LibraryDocument | null>(null);
  const [editForm, setEditForm] = useState<FormState>(EMPTY_FORM);
  const [replaceFile, setReplaceFile] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);

  function openEdit(doc: LibraryDocument) {
    setEditing(doc);
    setEditForm({
      title: doc.title,
      description: doc.description ?? "",
      category: doc.category ?? "",
      tags: doc.tags.join(", "),
      author_source: doc.author_source ?? "",
      visibility: doc.visibility,
      is_featured: doc.is_featured,
      review_status: doc.review_status,
    });
    setReplaceFile(null);
  }

  async function handleSaveEdit() {
    if (!editing) return;
    if (!editForm.title.trim()) return toast.error("Title is required");
    setSaving(true);
    try {
      let newFile: { path: string; name: string; size: number } | null = null;
      if (replaceFile) {
        const up = await uploadLibraryDocumentFile(replaceFile);
        newFile = { path: up.path, name: up.name, size: up.size };
      }
      await updateFn({
        data: {
          id: editing.id,
          title: editForm.title.trim(),
          description: editForm.description.trim() || null,
          category: editForm.category.trim() || null,
          tags: editForm.tags
            .split(",")
            .map((t) => t.trim())
            .filter(Boolean),
          author_source: editForm.author_source.trim() || null,
          visibility: editForm.visibility,
          is_featured: editForm.is_featured,
          review_status: editForm.review_status,
          ...(newFile
            ? {
                new_file_path: newFile.path,
                new_file_name: newFile.name,
                new_file_size: newFile.size,
                old_file_path: editing.file_path,
              }
            : {}),
        },
      });
      toast.success("Document updated");
      setEditing(null);
      qc.invalidateQueries({ queryKey: ["admin", "library-documents"] });
    } catch (e: any) {
      toast.error(e?.message ?? "Update failed");
    } finally {
      setSaving(false);
    }
  }

  const deleteMut = useMutation({
    mutationFn: async (id: string) => deleteFn({ data: { id } }),
    onSuccess: () => {
      toast.success("Document deleted");
      qc.invalidateQueries({ queryKey: ["admin", "library-documents"] });
    },
    onError: (e: any) => toast.error(e?.message ?? "Delete failed"),
  });

  async function togglePublish(doc: LibraryDocument) {
    const next: LibraryVisibility = doc.visibility === "draft" ? "public" : "draft";
    try {
      await updateFn({ data: { id: doc.id, visibility: next } });
      toast.success(next === "draft" ? "Unpublished" : "Published");
      qc.invalidateQueries({ queryKey: ["admin", "library-documents"] });
    } catch (e: any) {
      toast.error(e?.message ?? "Update failed");
    }
  }

  async function markReviewed(doc: LibraryDocument, status: LibraryReviewStatus) {
    try {
      await updateFn({ data: { id: doc.id, review_status: status } });
      toast.success(`Marked ${reviewLabel(status).toLowerCase()}`);
      qc.invalidateQueries({ queryKey: ["admin", "library-documents"] });
    } catch (e: any) {
      toast.error(e?.message ?? "Update failed");
    }
  }

  async function handleDownload(doc: LibraryDocument) {
    try {
      const { url } = await downloadFn({ data: { id: doc.id } });
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (e: any) {
      toast.error(e?.message ?? "Could not open file");
    }
  }

  return (
    <div className="min-h-dvh bg-background text-foreground">
      <div className="mx-auto max-w-6xl px-4 py-6 sm:py-10">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
          <div>
            <Link
              to="/admin"
              className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
            >
              <ArrowLeft className="h-4 w-4" /> Back to admin
            </Link>
            <h1 className="mt-2 text-2xl font-semibold sm:text-3xl">Library Documents</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Upload PDFs, worksheets, guides and images to the Recovery Trauma™ Library. Max {MAX_MB} MB per file.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => refetch()} disabled={isFetching}>
              <RefreshCw className={`me-2 h-4 w-4 ${isFetching ? "animate-spin" : ""}`} /> Refresh
            </Button>
            <Button onClick={() => setUploadOpen(true)}>
              <Upload className="me-2 h-4 w-4" /> Upload document
            </Button>
          </div>
        </div>

        <Card className="mb-4">
          <CardContent className="flex flex-wrap items-center gap-3 py-4">
            <div className="relative min-w-[220px] flex-1">
              <Input
                placeholder="Search title, description, tags…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            </div>
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-muted-foreground" />
              <Select value={visibilityFilter} onValueChange={(v) => setVisibilityFilter(v as any)}>
                <SelectTrigger className="w-[220px]">
                  <SelectValue placeholder="Visibility" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All visibility</SelectItem>
                  {VISIBILITY_OPTS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="ms-auto text-sm text-muted-foreground">
              {filtered.length} of {docs.length}
            </div>
          </CardContent>
        </Card>

        {isLoading ? (
          <div className="flex items-center gap-2 py-16 text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : filtered.length === 0 ? (
          <Card>
            <CardContent className="py-16 text-center text-muted-foreground">
              <FileText className="mx-auto mb-3 h-8 w-8 opacity-50" />
              No documents match. Upload your first PDF or worksheet.
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {filtered.map((doc) => (
              <Card key={doc.id}>
                <CardContent className="flex flex-col gap-4 py-4 sm:flex-row sm:items-start sm:justify-between">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="truncate text-base font-semibold">{doc.title}</h3>
                      {doc.is_featured && (
                        <Badge variant="secondary" className="gap-1">
                          <Star className="h-3 w-3" /> Featured
                        </Badge>
                      )}
                      <Badge variant={visibilityTone(doc.visibility)}>{visibilityLabel(doc.visibility)}</Badge>
                      <Badge variant="outline">{reviewLabel(doc.review_status)}</Badge>
                      <Badge variant="outline" className="uppercase">{doc.file_extension}</Badge>
                    </div>
                    {doc.description && (
                      <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">{doc.description}</p>
                    )}
                    <div className="mt-2 flex flex-wrap gap-3 text-xs text-muted-foreground">
                      {doc.category && <span>{doc.category}</span>}
                      {doc.author_source && <span>· {doc.author_source}</span>}
                      <span>· {bytesFmt(doc.file_size)}</span>
                      <span>· {new Date(doc.created_at).toLocaleDateString()}</span>
                      {doc.download_count > 0 && <span>· {doc.download_count} downloads</span>}
                    </div>
                    {doc.tags.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1">
                        {doc.tags.map((t) => (
                          <Badge key={t} variant="outline" className="text-xs">{t}</Badge>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center gap-2 sm:justify-end">
                    <Button variant="ghost" size="sm" onClick={() => handleDownload(doc)}>
                      <Download className="me-1 h-4 w-4" /> View
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => openEdit(doc)}>
                      <Edit className="me-1 h-4 w-4" /> Edit
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => togglePublish(doc)}>
                      {doc.visibility === "draft" ? "Publish" : "Unpublish"}
                    </Button>
                    {doc.review_status !== "approved" && (
                      <Button variant="outline" size="sm" onClick={() => markReviewed(doc, "approved")}>
                        Approve
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-destructive hover:text-destructive"
                      onClick={() => {
                        if (confirm(`Delete "${doc.title}"? This cannot be undone.`)) {
                          deleteMut.mutate(doc.id);
                        }
                      }}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Upload dialog */}
      <Dialog open={uploadOpen} onOpenChange={(o) => { if (!o) { setUploadOpen(false); resetUpload(); } }}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Upload document</DialogTitle>
            <DialogDescription>
              PDF, DOC, DOCX, PNG, JPG or WEBP · up to {MAX_MB} MB.
            </DialogDescription>
          </DialogHeader>
          <DocumentForm
            form={uploadForm}
            onChange={setUploadForm}
            file={uploadFile}
            onFileChange={setUploadFile}
            fileInputRef={uploadInputRef}
            replaceMode={false}
          />
          <DialogFooter>
            <Button variant="ghost" onClick={() => { setUploadOpen(false); resetUpload(); }}>
              Cancel
            </Button>
            <Button onClick={handleCreate} disabled={uploading}>
              {uploading ? <Loader2 className="me-2 h-4 w-4 animate-spin" /> : <Upload className="me-2 h-4 w-4" />}
              Upload
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => { if (!o) setEditing(null); }}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit document</DialogTitle>
            <DialogDescription>
              {editing?.file_name} · {editing && bytesFmt(editing.file_size)}
            </DialogDescription>
          </DialogHeader>
          <DocumentForm
            form={editForm}
            onChange={setEditForm}
            file={replaceFile}
            onFileChange={setReplaceFile}
            replaceMode
          />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={handleSaveEdit} disabled={saving}>
              {saving && <Loader2 className="me-2 h-4 w-4 animate-spin" />}
              Save changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function DocumentForm({
  form,
  onChange,
  file,
  onFileChange,
  fileInputRef,
  replaceMode,
}: {
  form: FormState;
  onChange: (f: FormState) => void;
  file: File | null;
  onFileChange: (f: File | null) => void;
  fileInputRef?: React.RefObject<HTMLInputElement | null>;
  replaceMode: boolean;
}) {
  return (
    <div className="grid gap-4 py-2">
      <div className="grid gap-2">
        <Label htmlFor="lib-file">
          {replaceMode ? "Replace file (optional)" : "File"}
        </Label>
        <input
          id="lib-file"
          ref={fileInputRef}
          type="file"
          accept={ACCEPT}
          onChange={(e) => onFileChange(e.target.files?.[0] ?? null)}
          className="text-sm"
        />
        {file && (
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <FileText className="h-3 w-3" />
            {file.name} · {bytesFmt(file.size)}
            <button type="button" onClick={() => onFileChange(null)} className="text-destructive">
              <X className="h-3 w-3" />
            </button>
          </div>
        )}
      </div>

      <div className="grid gap-2">
        <Label htmlFor="lib-title">Title</Label>
        <Input
          id="lib-title"
          value={form.title}
          onChange={(e) => onChange({ ...form, title: e.target.value })}
          maxLength={200}
          required
        />
      </div>

      <div className="grid gap-2">
        <Label htmlFor="lib-desc">Description</Label>
        <Textarea
          id="lib-desc"
          value={form.description}
          onChange={(e) => onChange({ ...form, description: e.target.value })}
          rows={3}
          maxLength={4000}
        />
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="grid gap-2">
          <Label htmlFor="lib-cat">Category</Label>
          <Input
            id="lib-cat"
            value={form.category}
            onChange={(e) => onChange({ ...form, category: e.target.value })}
            placeholder="e.g. Nervous system, Boundaries"
          />
        </div>
        <div className="grid gap-2">
          <Label htmlFor="lib-author">Author / source</Label>
          <Input
            id="lib-author"
            value={form.author_source}
            onChange={(e) => onChange({ ...form, author_source: e.target.value })}
            placeholder="Optional attribution"
          />
        </div>
      </div>

      <div className="grid gap-2">
        <Label htmlFor="lib-tags">Tags</Label>
        <Input
          id="lib-tags"
          value={form.tags}
          onChange={(e) => onChange({ ...form, tags: e.target.value })}
          placeholder="Comma-separated: cptsd, grounding, workbook"
        />
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="grid gap-2">
          <Label>Visibility</Label>
          <Select value={form.visibility} onValueChange={(v) => onChange({ ...form, visibility: v as LibraryVisibility })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {VISIBILITY_OPTS.map((o) => (
                <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="grid gap-2">
          <Label>Clinical review</Label>
          <Select value={form.review_status} onValueChange={(v) => onChange({ ...form, review_status: v as LibraryReviewStatus })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {REVIEW_OPTS.map((o) => (
                <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <label className="flex items-center gap-2 text-sm">
        <Checkbox
          checked={form.is_featured}
          onCheckedChange={(v) => onChange({ ...form, is_featured: Boolean(v) })}
        />
        Featured — highlight this document at the top of the Library
      </label>
    </div>
  );
}
